import React, { useMemo, useState } from "react";
import {
  Box, Stack, Typography, Button, TextField, ToggleButton, ToggleButtonGroup,
  Chip, Divider, IconButton, Pagination, Alert, CircularProgress, alpha, useTheme,
} from "@mui/material";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import SmsOutlinedIcon from "@mui/icons-material/SmsOutlined";
import SendIcon from "@mui/icons-material/Send";
import ReplayIcon from "@mui/icons-material/Replay";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import dayjs from "dayjs";
import {
  useGetParentEmailRecordsQuery,
  useCreateParentEmailRecordMutation,
  useResentEmailParentMutation,
  useGetParentSmsRecordsQuery,
  useCreateParentSmsRecordMutation,
  useResentSmsParentMutation,
} from "../../../Api/attoDeskApi";
import { useNotifier } from "../../../Core/Notifier";
import {
  formatPhoneNumberForDisplay,
  normalizePhoneNumber,
  isValidPhoneNumber,
} from "../../../Components/Validation/validationUtils";

interface Props {
  parentId: number | string;
  parentEmail: string | null | undefined;
  parentPhone: string | null | undefined; // digits-only from API
  parentName: string;
}

type Channel = "email" | "sms";
type HistoryFilter = "all" | "email" | "sms";

const isEmail = (v: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v.trim());

const StatusChip: React.FC<{ status: string }> = ({ status }) => {
  const s = (status || "").toLowerCase();
  const cfg =
    s === "sent"
      ? { color: "#16a34a", bg: "#dcfce7", label: "Sent", icon: <CheckCircleIcon sx={{ fontSize: 12 }} /> }
      : s === "queued"
      ? { color: "#0369a1", bg: "#e0f2fe", label: "Queued", icon: <CheckCircleIcon sx={{ fontSize: 12 }} /> }
      : s === "suppressed"
      ? { color: "#7c2d12", bg: "#fef3c7", label: "Suppressed", icon: <ErrorOutlineIcon sx={{ fontSize: 12 }} /> }
      : s === "failed"
      ? { color: "#dc2626", bg: "#fee2e2", label: "Failed", icon: <ErrorOutlineIcon sx={{ fontSize: 12 }} /> }
      : s === "no email found"
      ? { color: "#9a3412", bg: "#ffedd5", label: "No email", icon: <ErrorOutlineIcon sx={{ fontSize: 12 }} /> }
      : { color: "#475569", bg: "#e2e8f0", label: status || "Pending", icon: null };
  return (
    <Chip
      size="small"
      icon={cfg.icon || undefined}
      label={cfg.label}
      sx={{ height: 22, fontSize: 11, fontWeight: 700, bgcolor: cfg.bg, color: cfg.color, "& .MuiChip-icon": { color: cfg.color } }}
    />
  );
};

const ParentCommunicationPanel: React.FC<Props> = ({ parentId, parentEmail, parentPhone, parentName }) => {
  const theme = useTheme();
  const { showMessage, showErrorMessage } = useNotifier();

  const [tab, setTab] = useState<Channel>("email");
  const [historyFilter, setHistoryFilter] = useState<HistoryFilter>("all");
  const [emailPage, setEmailPage] = useState(1);
  const [smsPage, setSmsPage] = useState(1);

  // Composers
  const [emailTo, setEmailTo] = useState<string>(parentEmail || "");
  const [emailSubject, setEmailSubject] = useState("");
  const [emailMessage, setEmailMessage] = useState("");

  const displayPhone = useMemo(() => formatPhoneNumberForDisplay(parentPhone || ""), [parentPhone]);
  const [smsTo, setSmsTo] = useState<string>(displayPhone);
  const [smsMessage, setSmsMessage] = useState("");

  React.useEffect(() => { setEmailTo(parentEmail || ""); }, [parentEmail]);
  React.useEffect(() => { setSmsTo(displayPhone); }, [displayPhone]);

  // History
  const { data: emailData, isFetching: emailLoading } = useGetParentEmailRecordsQuery(
    { parent_id: parentId, page: emailPage, perPage: 10 },
    { skip: !parentId }
  );
  const { data: smsData, isFetching: smsLoading } = useGetParentSmsRecordsQuery(
    { parent_id: parentId, page: smsPage, perPage: 10 },
    { skip: !parentId }
  );

  const [sendEmail, { isLoading: sendingEmail }] = useCreateParentEmailRecordMutation();
  const [sendSms, { isLoading: sendingSms }] = useCreateParentSmsRecordMutation();
  const [resendEmail, { isLoading: resendingEmail }] = useResentEmailParentMutation();
  const [resendSms, { isLoading: resendingSms }] = useResentSmsParentMutation();

  const submitEmail = async () => {
    if (!parentEmail) { showErrorMessage("No email address is available for this parent."); return; }
    if (!isEmail(emailTo)) { showErrorMessage("Please enter a valid email address."); return; }
    if (!emailSubject.trim()) { showErrorMessage("Subject is required."); return; }
    if (!emailMessage.trim()) { showErrorMessage("Message is required."); return; }
    try {
      const r: any = await sendEmail({
        parent_id: parentId,
        toEmail: emailTo.trim(),
        subject: emailSubject.trim(),
        message: emailMessage,
      }).unwrap();
      if (r?.status) {
        showMessage("Email sent successfully.");
        setEmailSubject("");
        setEmailMessage("");
      } else {
        showErrorMessage(r?.message || "Email could not be sent.");
      }
    } catch (err: any) {
      showErrorMessage(err?.data?.message || "Email could not be sent.");
    }
  };

  const submitSms = async () => {
    if (!parentPhone) { showErrorMessage("No phone number is available for this parent."); return; }
    if (!isValidPhoneNumber(smsTo)) { showErrorMessage("Please enter a valid 10-digit phone number."); return; }
    if (!smsMessage.trim()) { showErrorMessage("Message is required."); return; }
    try {
      const normalized = normalizePhoneNumber(smsTo);
      const r: any = await sendSms({
        parent_id: parentId,
        toMobileNumber: normalized, // backend TwilioService adds +1
        message: smsMessage,
      }).unwrap();
      if (r?.status) {
        showMessage("SMS sent successfully.");
        setSmsMessage("");
      } else {
        showErrorMessage(r?.message || "SMS could not be sent.");
      }
    } catch (err: any) {
      showErrorMessage(err?.data?.message || "SMS could not be sent.");
    }
  };

  const emailRows: any[] = (emailData as any)?.data || [];
  const smsRows: any[] = (smsData as any)?.data || [];
  const emailMeta: any = (emailData as any)?.meta || {};
  const smsMeta: any = (smsData as any)?.meta || {};

  const merged = useMemo(() => {
    const withKind = [
      ...emailRows.map((r) => ({ ...r, __kind: "email" as const })),
      ...smsRows.map((r) => ({ ...r, __kind: "sms" as const })),
    ];
    const filtered =
      historyFilter === "all" ? withKind
      : historyFilter === "email" ? withKind.filter((r) => r.__kind === "email")
      : withKind.filter((r) => r.__kind === "sms");
    return filtered.sort((a, b) => (dayjs(b.created_at).valueOf() - dayjs(a.created_at).valueOf()));
  }, [emailRows, smsRows, historyFilter]);

  const doResend = async (row: any) => {
    try {
      const r: any = row.__kind === "email"
        ? await resendEmail(String(row.id)).unwrap()
        : await resendSms(String(row.id)).unwrap();
      if (r?.status) showMessage(row.__kind === "email" ? "Email resent successfully." : "SMS resent successfully.");
      else showErrorMessage(r?.message || "Resend failed.");
    } catch (err: any) {
      showErrorMessage(err?.data?.message || "Resend failed.");
    }
  };

  const cardSx = {
    borderRadius: "16px",
    border: "1px solid rgba(15,23,42,0.06)",
    bgcolor: "#fff",
    p: 2.5,
  };

  return (
    <Box sx={{ mb: 2.5 }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1.25 }}>
        <Box>
          <Typography sx={{ fontSize: 15, fontWeight: 800 }}>Communication</Typography>
          <Typography sx={{ fontSize: 12, color: "text.secondary" }}>
            Send email or SMS directly to {parentName || "this parent"} and review communication history.
          </Typography>
        </Box>
      </Stack>

      <Box sx={{
        display: "grid",
        gridTemplateColumns: { xs: "1fr", md: "minmax(0, 5fr) minmax(0, 6fr)" },
        gap: 2,
      }}>
        {/* ── Composer ── */}
        <Box sx={cardSx}>
          <ToggleButtonGroup
            exclusive
            value={tab}
            onChange={(_, v) => v && setTab(v)}
            size="small"
            sx={{ mb: 2 }}
          >
            <ToggleButton value="email" sx={{ textTransform: "none", px: 2, fontWeight: 700 }}>
              <EmailOutlinedIcon sx={{ fontSize: 16, mr: 0.75 }} /> Email
            </ToggleButton>
            <ToggleButton value="sms" sx={{ textTransform: "none", px: 2, fontWeight: 700 }}>
              <SmsOutlinedIcon sx={{ fontSize: 16, mr: 0.75 }} /> SMS
            </ToggleButton>
          </ToggleButtonGroup>

          {tab === "email" ? (
            !parentEmail ? (
              <Alert severity="warning" sx={{ borderRadius: "10px" }}>
                No email address is available for this parent. Edit the parent to add one before sending email.
              </Alert>
            ) : (
              <Stack spacing={1.5}>
                <TextField
                  label="To"
                  size="small"
                  fullWidth
                  value={emailTo}
                  onChange={(e) => setEmailTo(e.target.value)}
                  required
                />
                <TextField
                  label="Subject"
                  size="small"
                  fullWidth
                  value={emailSubject}
                  onChange={(e) => setEmailSubject(e.target.value)}
                  required
                  inputProps={{ maxLength: 255 }}
                />
                <TextField
                  label="Message"
                  size="small"
                  fullWidth
                  multiline
                  minRows={6}
                  value={emailMessage}
                  onChange={(e) => setEmailMessage(e.target.value)}
                  required
                />
                <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1, pt: 0.5 }}>
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={() => { setEmailSubject(""); setEmailMessage(""); setEmailTo(parentEmail || ""); }}
                    disabled={sendingEmail}
                    sx={{ textTransform: "none", borderRadius: "10px" }}
                  >
                    Reset
                  </Button>
                  <Button
                    variant="contained"
                    size="small"
                    onClick={submitEmail}
                    disabled={sendingEmail}
                    startIcon={sendingEmail ? <CircularProgress size={14} sx={{ color: "#fff" }} /> : <SendIcon sx={{ fontSize: 16 }} />}
                    sx={{ textTransform: "none", borderRadius: "10px", fontWeight: 700 }}
                  >
                    Send Email
                  </Button>
                </Box>
              </Stack>
            )
          ) : (
            !parentPhone ? (
              <Alert severity="warning" sx={{ borderRadius: "10px" }}>
                No phone number is available for this parent. Edit the parent to add one before sending SMS.
              </Alert>
            ) : (
              <Stack spacing={1.5}>
                <TextField
                  label="To"
                  size="small"
                  fullWidth
                  value={smsTo}
                  onChange={(e) => setSmsTo(formatPhoneNumberForDisplay(e.target.value.replace(/\D/g, "").slice(0, 10)))}
                  helperText="Sent as +1XXXXXXXXXX"
                  required
                />
                <TextField
                  label="Message"
                  size="small"
                  fullWidth
                  multiline
                  minRows={6}
                  value={smsMessage}
                  onChange={(e) => setSmsMessage(e.target.value.slice(0, 1600))}
                  helperText={`${smsMessage.length}/1600`}
                  required
                />
                <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1, pt: 0.5 }}>
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={() => { setSmsMessage(""); setSmsTo(displayPhone); }}
                    disabled={sendingSms}
                    sx={{ textTransform: "none", borderRadius: "10px" }}
                  >
                    Reset
                  </Button>
                  <Button
                    variant="contained"
                    size="small"
                    onClick={submitSms}
                    disabled={sendingSms}
                    startIcon={sendingSms ? <CircularProgress size={14} sx={{ color: "#fff" }} /> : <SendIcon sx={{ fontSize: 16 }} />}
                    sx={{ textTransform: "none", borderRadius: "10px", fontWeight: 700 }}
                  >
                    Send SMS
                  </Button>
                </Box>
              </Stack>
            )
          )}
        </Box>

        {/* ── History ── */}
        <Box sx={cardSx}>
          <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1.25 }}>
            <Typography sx={{ fontSize: 13.5, fontWeight: 800 }}>Communication History</Typography>
            <ToggleButtonGroup
              exclusive
              value={historyFilter}
              onChange={(_, v) => v && setHistoryFilter(v)}
              size="small"
            >
              <ToggleButton value="all" sx={{ textTransform: "none", px: 1.5, fontSize: 11, fontWeight: 700 }}>All</ToggleButton>
              <ToggleButton value="email" sx={{ textTransform: "none", px: 1.5, fontSize: 11, fontWeight: 700 }}>Email</ToggleButton>
              <ToggleButton value="sms" sx={{ textTransform: "none", px: 1.5, fontSize: 11, fontWeight: 700 }}>SMS</ToggleButton>
            </ToggleButtonGroup>
          </Stack>

          {(emailLoading || smsLoading) && merged.length === 0 ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
              <CircularProgress size={22} />
            </Box>
          ) : merged.length === 0 ? (
            <Box sx={{
              textAlign: "center", py: 4, border: "1px dashed rgba(15,23,42,0.15)",
              borderRadius: "12px", color: "text.disabled",
            }}>
              <Typography sx={{ fontSize: 13 }}>No messages sent yet.</Typography>
            </Box>
          ) : (
            <Stack spacing={1.25} sx={{ maxHeight: 520, overflowY: "auto", pr: 0.5 }}>
              {merged.map((row: any) => {
                const isEmailRow = row.__kind === "email";
                const st = (row.status || "").toLowerCase();
                const isFailed = st === "failed" || st === "no email found";
                return (
                  <Box
                    key={`${row.__kind}-${row.id}`}
                    sx={{
                      border: "1px solid rgba(15,23,42,0.06)",
                      borderRadius: "12px",
                      p: 1.5,
                      bgcolor: "#f8fafc",
                    }}
                  >
                    <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 0.5 }}>
                      <Stack direction="row" alignItems="center" spacing={1}>
                        {isEmailRow
                          ? <EmailOutlinedIcon sx={{ fontSize: 16, color: theme.palette.primary.main }} />
                          : <SmsOutlinedIcon sx={{ fontSize: 16, color: theme.palette.primary.main }} />}
                        <Typography sx={{ fontSize: 12.5, fontWeight: 800 }}>
                          {isEmailRow ? "Email sent" : "SMS sent"}
                        </Typography>
                        <StatusChip status={row.status || "Pending"} />
                      </Stack>
                      {isFailed && (
                        <IconButton
                          size="small"
                          onClick={() => doResend(row)}
                          disabled={isEmailRow ? resendingEmail : resendingSms}
                          title="Resend"
                          sx={{ color: theme.palette.primary.main }}
                        >
                          <ReplayIcon sx={{ fontSize: 16 }} />
                        </IconButton>
                      )}
                    </Stack>
                    <Typography sx={{ fontSize: 11.5, color: "text.secondary", mb: 0.5 }}>
                      {row.created_at ? dayjs(row.created_at).format("MMM D, YYYY · h:mm A") : ""}
                    </Typography>
                    <Typography sx={{ fontSize: 12, color: "text.secondary" }}>
                      To:{" "}
                      <Box component="span" sx={{ color: "text.primary", fontWeight: 600 }}>
                        {isEmailRow ? row.toEmail : formatPhoneNumberForDisplay(row.toMobileNumber || "")}
                      </Box>
                    </Typography>
                    {isEmailRow && row.subject && (
                      <Typography sx={{ fontSize: 12.5, fontWeight: 700, mt: 0.5 }}>
                        {row.subject}
                      </Typography>
                    )}
                    {row.message && (
                      <Typography sx={{
                        fontSize: 12.5, mt: 0.5, color: "text.primary",
                        display: "-webkit-box", WebkitLineClamp: 3, WebkitBoxOrient: "vertical",
                        overflow: "hidden",
                      }}>
                        {String(row.message).replace(/<[^>]+>/g, " ")}
                      </Typography>
                    )}
                    {row.error_message && isFailed && (
                      <Typography sx={{ fontSize: 11.5, color: "#dc2626", mt: 0.5 }}>
                        {row.error_message}
                      </Typography>
                    )}
                    <Divider sx={{ my: 0.75 }} />
                    <Typography sx={{ fontSize: 11, color: "text.disabled" }}>
                      Sent by {row.sender_name || "Unknown"}
                    </Typography>
                  </Box>
                );
              })}
            </Stack>
          )}

          {/* Pagination for whichever channel matches the filter */}
          {(historyFilter === "email" && (emailMeta.lastPage || 1) > 1) && (
            <Box sx={{ display: "flex", justifyContent: "center", mt: 1.5 }}>
              <Pagination
                size="small"
                count={emailMeta.lastPage || 1}
                page={emailPage}
                onChange={(_, p) => setEmailPage(p)}
              />
            </Box>
          )}
          {(historyFilter === "sms" && (smsMeta.lastPage || 1) > 1) && (
            <Box sx={{ display: "flex", justifyContent: "center", mt: 1.5 }}>
              <Pagination
                size="small"
                count={smsMeta.lastPage || 1}
                page={smsPage}
                onChange={(_, p) => setSmsPage(p)}
              />
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default ParentCommunicationPanel;
